import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X, Eye, EyeOff, Mail, Lock, User, Phone } from 'lucide-react';
import { getBackendUrl, fetchWithRetry, safeJson } from '../../utils/api';
import { useBrand } from '../../contexts/BrandContext';

const AuthModal = ({ mode, onClose, onLogin, onSwitchMode, language }) => {
  const { brandName } = useBrand();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const { tt } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isConnectionError, setIsConnectionError] = useState(false);

  const translations = {
    ru: {
      login: 'Вход',
      register: 'Регистрация',
      name: 'Полное имя',
      email: 'Email адрес',
      emailOrPhone: 'Email или номер телефона',
      emailOrPhoneHint: 'Можно входить по email или номеру телефона (например +7…)',
      emailOrPhonePlaceholder: 'example@email.com  или  +71234567890',
      phone: 'Номер телефона',
      password: 'Пароль',
      confirmPassword: 'Подтвердите пароль',
      loginButton: 'Войти',
      registerButton: 'Зарегистрироваться',
      switchToRegister: 'Нет аккаунта? Зарегистрируйтесь',
      switchToLogin: 'Уже есть аккаунт? Войдите',
      forgotPassword: 'Забыли пароль?',
      orDivider: 'или',
      welcomeBack: 'Добро пожаловать обратно!',
      welcomeTitle: `Присоединяйтесь к ${brandName}`,
      loginSubtitle: 'Войдите в свой аккаунт чтобы продолжить торговлю',
      registerSubtitle: 'Создайте аккаунт чтобы начать торговать'
    },
    en: {
      login: 'Login',
      register: 'Register',
      name: 'Full Name',
      email: 'Email Address',
      emailOrPhone: 'Email or phone number',
      emailOrPhoneHint: 'You can log in with either your email or your phone (e.g. +1…)',
      emailOrPhonePlaceholder: 'example@email.com  or  +12345678901',
      phone: 'Phone Number',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      loginButton: 'Sign In',
      registerButton: 'Create Account',
      switchToRegister: 'No account? Sign up',
      switchToLogin: 'Already have an account? Sign in',
      forgotPassword: 'Forgot password?',
      orDivider: 'or',
      welcomeBack: 'Welcome back!',
      welcomeTitle: `Join ${brandName}`,
      loginSubtitle: 'Sign in to your account to continue trading',
      registerSubtitle: 'Create an account to start trading'
    },
    kk: {
      login: 'Кіру',
      register: 'Тіркелу',
      name: 'Толық аты',
      email: 'Email мекенжайы',
      emailOrPhone: 'Email немесе телефон нөмірі',
      emailOrPhoneHint: 'Email немесе телефон нөмірі арқылы кіруге болады (мысалы +7…)',
      emailOrPhonePlaceholder: 'example@email.com  немесе  +71234567890',
      phone: 'Телефон нөмірі',
      password: 'Құпия сөз',
      confirmPassword: 'Құпия сөзді растаңыз',
      loginButton: 'Кіру',
      registerButton: 'Тіркелу',
      switchToRegister: 'Аккаунт жоқ па? Тіркеліңіз',
      switchToLogin: 'Аккаунт бар ма? Кіріңіз',
      forgotPassword: 'Құпия сөзді ұмыттыңыз ба?',
      orDivider: 'немесе',
      welcomeBack: 'Қайта келуіңізбен!',
      welcomeTitle: `${brandName}-қа қосылыңыз`,
      loginSubtitle: 'Сауданы жалғастыру үшін аккаунтыңызға кіріңіз',
      registerSubtitle: 'Сауданы бастау үшін аккаунт жасаңыз'
    }
  };

  const t = translations[language] || translations['ru'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const BACKEND_URL = getBackendUrl();
      
      if (mode === 'register') {
        // Валидация полей
        if (formData.password !== formData.confirmPassword) {
          setError(language === 'ru' ? 'Пароли не совпадают' : 'Passwords do not match');
          setLoading(false);
          return;
        }

        // Валидация email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError(language === 'ru' ? 'Введите корректный email адрес' : 'Please enter a valid email address');
          setLoading(false);
          return;
        }

        // Валидация номера телефона (базовая)
        if (formData.phone && formData.phone.length < 10) {
          setError(language === 'ru' ? 'Введите корректный номер телефона' : 'Please enter a valid phone number');
          setLoading(false);
          return;
        }

        // Валидация пароля
        if (formData.password.length < 6) {
          setError(language === 'ru' ? 'Пароль должен содержать минимум 6 символов' : 'Password must be at least 6 characters long');
          setLoading(false);
          return;
        }

        const response = await fetchWithRetry(`${BACKEND_URL}/api/auth/register-client`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password
          }),
        });

        if (!response.ok) {
          const data = await safeJson(response);
          let errorMessage = data.detail || 'Registration failed';
          if (data.detail?.includes('email уже зарегистрирован')) {
            errorMessage = language === 'ru' ? 'Пользователь с таким email уже зарегистрирован' : 'User with this email is already registered';
          } else if (data.detail?.includes('номером телефона уже зарегистрирован')) {
            errorMessage = language === 'ru' ? 'Пользователь с таким номером телефона уже зарегистрирован' : 'User with this phone number is already registered';
          } else if (data.detail?.includes('номером телефона уже существует')) {
            errorMessage = language === 'ru' ? 'Клиент с таким номером телефона уже существует' : 'Client with this phone number already exists';
          }
          throw new Error(errorMessage);
        }

        const data = await safeJson(response);
        onLogin(data.user, data.access_token);
      } else {
        console.log('🔐 AuthModal: Starting login process...');
        console.log('🌐 AuthModal: Backend URL:', BACKEND_URL);
        console.log('📧 AuthModal: Login credentials:', { email: formData.email, password: formData.password ? 'provided' : 'missing' });
        
        const response = await fetchWithRetry(`${BACKEND_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });

        console.log('📊 AuthModal: Login API response status:', response.status);

        if (!response.ok) {
          const data = await safeJson(response);
          console.error('❌ AuthModal: Login failed:', data);
          throw new Error(data.detail || 'Login failed');
        }

        const data = await safeJson(response);
        console.log('✅ AuthModal: Login successful, data received:', data);
        console.log('👤 AuthModal: User data:', data.user);
        console.log('🎫 AuthModal: Token:', data.access_token ? 'received' : 'missing');
        
        console.log('🚀 AuthModal: Calling onLogin with user and token...');
        onLogin(data.user, data.access_token);
        console.log('✅ AuthModal: onLogin called successfully');
      }
    } catch (error) {
      if (error.message === 'Failed to fetch' || error.name === 'AbortError') {
        setIsConnectionError(true);
        setError('Ошибка соединения с сервером. Возможно, у вас устаревшая версия приложения.');
      } else {
        setIsConnectionError(false);
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'login' ? t.welcomeBack : t.welcomeTitle}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {mode === 'login' ? t.loginSubtitle : t.registerSubtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              <div>{error}</div>
              {isConnectionError && (
                <button
                  type="button"
                  onClick={() => { if (window.forceAppUpdate) window.forceAppUpdate(); else window.location.reload(true); }}
                  data-testid="force-update-btn"
                  className="mt-2 w-full bg-red-600 text-white text-xs font-semibold py-2 px-3 rounded-md hover:bg-red-700 transition-colors"
                >
                  Обновить приложение
                </button>
              )}
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.name}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  placeholder={tt('Введите ваше имя')}
                  required
                />
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.phone}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  placeholder="+7 (999) 123-45-67"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {mode === 'login' ? t.emailOrPhone : t.email}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={mode === 'login' ? 'text' : 'email'}
                inputMode={mode === 'login' ? 'text' : 'email'}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={mode === 'login' ? t.emailOrPhonePlaceholder : 'example@email.com'}
                required
              />
            </div>
            {mode === 'login' && (
              <p className="text-xs text-gray-500 mt-1.5 leading-snug">{t.emailOrPhoneHint}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.password}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.confirmPassword}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
          )}

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                {t.forgotPassword}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {mode === 'login' ? 'Входим...' : 'Регистрируемся...'}
              </div>
            ) : (
              mode === 'login' ? t.loginButton : t.registerButton
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              {mode === 'login' ? t.switchToRegister : t.switchToLogin}
            </p>
            <button
              onClick={() => onSwitchMode(mode === 'login' ? 'register' : 'login')}
              className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors mt-1"
            >
              {mode === 'login' ? t.register : t.login}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;